import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateCachedSessionToken } from '../utils/api';
import ZaggerLogo from './ZaggerLogo';
import DataHorizonButton from './DataHorizonButton';
import './NavigationMenu.css';

// Unified navigation menu with 3 main sections:
// 1) Top: close/open button
// 2) Links/Buttons: default Projects + Settings (can be extended per page)
// 3) Additional content: page-specific content (e.g., sessions list)
const NavigationMenu = ({
  isOpen,
  onToggle,
  // When true, disable CSS transitions (useful for first paint based on cookie)
  disableTransition = false,
  // Back-compat: additional action items (non-link or custom) that appear after the default links
  menuItems = [],
  // Current active id for non-link items
  activeItem,
  onItemClick,
  // Optional override or extension for link buttons. If not provided, defaults to Projects/Settings
  linkItems,
  // Optional additional content area (e.g., sessions list)
  extraContent,
  // Whether to show user menu items (logout, dark mode) at the bottom
  showUserMenu = true,
}) => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item.id);
    }
    if (item.onClick) {
      item.onClick();
    }
  };

  const defaultLinks = [
    { id: 'projects', label: 'Projects', icon: 'fas fa-folder-open', to: '/projects' },
    { id: 'horizon', label: 'Horizon', icon: 'fas fa-layer-group', to: '/horizon', subItems: [] },
    // Use base /settings so it remains active across any settings sub-routes
    { id: 'settings', label: 'Settings', icon: 'fas fa-gear', to: '/settings', subItems: [] },
    { id: 'help', label: 'Help', icon: 'fas fa-circle-question', to: '/help/docs', subItems: [] },
  ];

  const links = linkItems && Array.isArray(linkItems) ? linkItems : defaultLinks;

  const location = useLocation();

  const isPathActive = (to, hardMatch = false) => {
    if (!to) return false;
    const { pathname } = location;
    if (hardMatch) {
      // Hard match: only active when the path exactly equals the target
      return pathname === to;
    }
    // Soft match (default): exact match or any nested route
    if (pathname === to) return true;
    // Normalize paths: remove trailing slashes for comparison
    const normalizedTo = to.endsWith('/') ? to.slice(0, -1) : to;
    const normalizedPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    // Check if pathname starts with the base path followed by a slash (or is exactly equal)
    return normalizedPath === normalizedTo || normalizedPath.startsWith(`${normalizedTo}/`);
  };

  const getSectionBasePath = (item) => {
    if (item.sectionBasePath) return item.sectionBasePath;
    if (!item.subItems?.length) return null;
    const paths = item.subItems.map((sub) => sub.to).filter(Boolean);
    if (!paths.length) return null;
    let common = paths[0];
    for (let i = 1; i < paths.length; i += 1) {
      let prefix = common;
      while (prefix && !paths[i].startsWith(prefix)) {
        const slash = prefix.lastIndexOf('/');
        prefix = slash > 0 ? prefix.slice(0, slash) : '';
      }
      common = prefix;
      if (!common) return null;
    }
    return common;
  };

  return (
    <nav className={`navigation-menu ${isOpen ? 'open' : 'closed'} ${disableTransition ? 'no-anim' : ''}`}>
      {/* Top Section: close/open button */}
      <div className="nav-top">
        <div className="nav-brand" aria-hidden={!isOpen}>
          <ZaggerLogo variant="icon" />
        </div>
        <button
          className="menu-toggle"
          onClick={onToggle}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      {user?.id && (
        <div className="nav-horizon-section">
          <DataHorizonButton layout="sidebar" />
        </div>
      )}

      {/* Links/Buttons Section */}
      <div className="nav-actions">
        <ul className="menu-items">
          {links.map((item) => {
            const hard = item?.hardMatch || item?.match === 'hard';
            const hasSubItems = item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0;
            const anySubActive = hasSubItems && item.subItems.some((sub) =>
              sub.to ? isPathActive(sub.to, false) : activeItem === sub.id
            );
            const sectionBasePath = getSectionBasePath(item);
            const sectionActive = sectionBasePath ? isPathActive(sectionBasePath, false) : false;
            const active = hasSubItems
              ? (anySubActive || sectionActive)
              : (item.to ? isPathActive(item.to, hard) : activeItem === item.id);
            // Keep sub-items visible while navigating between sibling routes in the same section
            const showSubItems = hasSubItems && active;
            const badge = item.badge;
            return (
              <li key={item.id} className={`menu-item ${active ? 'active' : ''} ${hasSubItems ? 'has-sub-items' : ''}`}>
                {item.to ? (
                  <NavLink to={item.to} className="menu-link">
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                    {badge > 0 && <span className={`menu-badge${badge >= 10 ? ' menu-badge-wide' : ''}`}>{badge}</span>}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className="menu-button"
                    onClick={() => handleItemClick(item)}
                  >
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                    {badge > 0 && <span className={`menu-badge${badge >= 10 ? ' menu-badge-wide' : ''}`}>{badge}</span>}
                  </button>
                )}
                {showSubItems && (
                  <ul className="menu-sub-items">
                    {item.subItems.map((subItem) => {
                      const subActive = subItem.to ? isPathActive(subItem.to, false) : activeItem === subItem.id;
                      const subBadge = subItem.badge;
                      return (
                        <li key={subItem.id} className={`menu-sub-item ${subActive ? 'active' : ''}`}>
                          {subItem.to ? (
                            <NavLink to={subItem.to} className="menu-link menu-sub-link">
                              <i className={subItem.icon}></i>
                              <span>{subItem.label}</span>
                              {subBadge > 0 && <span className={`menu-badge menu-sub-badge${subBadge >= 10 ? ' menu-badge-wide' : ''}`}>{subBadge}</span>}
                            </NavLink>
                          ) : (
                            <button
                              type="button"
                              className="menu-button menu-sub-button"
                              onClick={() => handleItemClick(subItem)}
                            >
                              <i className={subItem.icon}></i>
                              <span>{subItem.label}</span>
                              {subBadge > 0 && <span className={`menu-badge menu-sub-badge${subBadge >= 10 ? ' menu-badge-wide' : ''}`}>{subBadge}</span>}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}

          {/* Back-compat / additional items (non-link or custom) */}
          {menuItems.map((item) => {
            if (item.isHeader) {
              return (
                <li key={item.id} className="menu-header-item">
                  <div className="menu-header-label">
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                  </div>
                </li>
              );
            }
            if (item.isDivider) {
              return <li key={item.id} className="menu-divider-item"></li>;
            }
            return (
              <li key={item.id} className="menu-item" onClick={() => handleItemClick(item)}>
                <div className={`menu-button ${activeItem === item.id ? 'active' : ''}`}>
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Additional content Section */}
      {extraContent && (
        <div className="nav-extra">
          {extraContent}
        </div>
      )}

      {/* User menu items (logout, dark mode) */}
      {showUserMenu && (
        <div className="nav-user-menu">
          <div className="nav-user-info">
            {user?.email && (
              <div className="user-email-display">
                <i className="fas fa-user"></i>
                <span>{user.email}</span>
                {user?.accountType && (
                  <span className="user-role-badge">{user.accountType}</span>
                )}
              </div>
            )}
          </div>
          <div className="nav-user-actions">
            <button
              className="menu-item menu-button"
              onClick={async () => {
                try {
                  // Clear cached session token
                  updateCachedSessionToken(null, null);
                  
                  // Sign out
                  await logout();
                  
                  // Redirect to login
                  navigate('/login', { replace: true });
                } catch (error) {
                  console.error('Error during logout:', error);
                  // Still redirect even if logout fails
                  navigate('/login', { replace: true });
                }
              }}
              disabled={!user || loading}
              title="Sign out"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationMenu;
